import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, Users, CreditCard } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { formatPrice } from "@/utils/formatPrice";

// Matches get-public-landing edge function response for event
interface EventEntity {
  name: string;
  description: string | null;
  image_url: string | null;
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  max_participants: number | null;
  member_price: number | null;
  guest_price: number | null;
  status: string;
  registered_count: number;
  spots_left: number | null;
}

interface Props {
  entity: Record<string, unknown>;
  onAction: (path: string) => void;
  brandColor?: string;
}

export default function EventLanding({ entity, onAction, brandColor }: Props) {
  const event = entity as unknown as EventEntity;
  const startDate = parseISO(event.start_date);
  const isSameDay = event.start_date === event.end_date;
  const isFull = event.spots_left !== null && event.spots_left <= 0;

  const memberPrice = event.member_price ?? 0;
  const guestPrice = event.guest_price ?? 0;

  return (
    <div className="space-y-4">
      {/* Hero image */}
      {event.image_url && (
        <div className="rounded-2xl overflow-hidden">
          <img src={event.image_url} alt={event.name} className="w-full h-48 object-cover" />
        </div>
      )}

      <div className="space-y-1">
        <h1 className="text-2xl font-bold">{event.name}</h1>
      </div>

      {event.description && (
        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
          {event.description}
        </p>
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>
              {isSameDay
                ? format(startDate, "EEEE d MMMM yyyy", { locale: sv })
                : `${format(startDate, "d MMM", { locale: sv })} – ${format(parseISO(event.end_date), "d MMM yyyy", { locale: sv })}`}
            </span>
          </div>
          {event.start_time && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>
                {event.start_time.slice(0, 5)}
                {event.end_time ? `–${event.end_time.slice(0, 5)}` : ""}
              </span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{event.location}</span>
            </div>
          )}
          {event.max_participants && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>
                {event.registered_count}/{event.max_participants} platser
                {event.spots_left != null && event.spots_left > 0 && (
                  <span className="text-muted-foreground"> ({event.spots_left} kvar)</span>
                )}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <p className="text-sm font-medium">Pris</p>
          <div className="flex items-center gap-2 text-sm">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span>
              Medlem: {memberPrice > 0 ? formatPrice(memberPrice) : "Gratis"}
            </span>
          </div>
          {guestPrice > 0 && guestPrice !== memberPrice && (
            <div className="flex items-center gap-2 text-sm">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              <span>Gäst: {formatPrice(guestPrice)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action */}
      <Button
        className="w-full text-base py-6"
        style={brandColor ? { backgroundColor: brandColor } : undefined}
        disabled={isFull || event.status !== "published"}
        onClick={() => onAction("/community")}
      >
        {isFull ? "Fullbokat" : "Anmäl dig →"}
      </Button>
    </div>
  );
}
